import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Code, AlertTriangle } from 'lucide-react';

/**
 * CustomCSSField - Code editor field for custom CSS input
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} [props.field.selector] - CSS selector for the element
 * @param {string} props.value - Current CSS value
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.componentId] - Component ID to derive selector from
 */
export default function CustomCSSField({
  field,
  value,
  onChange,
  disabled = false,
  componentId,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Derive selector from field.selector or componentId
  const selector = field.selector || (componentId ? `#${componentId}` : '.element');

  // Basic CSS validation
  const cssWarning = useMemo(() => {
    if (!value || value.trim() === '') return null;

    const trimmedValue = value.trim();

    // Check for unbalanced braces (common mistake)
    const openBraces = (trimmedValue.match(/{/g) || []).length;
    const closeBraces = (trimmedValue.match(/}/g) || []).length;
    if (openBraces !== closeBraces) {
      return 'Unbalanced braces detected';
    }

    // Check for unclosed comments
    const openComments = (trimmedValue.match(/\/\*/g) || []).length;
    const closeComments = (trimmedValue.match(/\*\//g) || []).length;
    if (openComments !== closeComments) {
      return 'Unclosed comment detected';
    }

    // Check for missing semicolons (basic heuristic)
    // Look for property: value patterns not ending with ; or }
    const lines = trimmedValue.split('\n');
    for (const line of lines) {
      const trimmedLine = line.trim();
      // Skip empty lines, comments, and lines ending with { or }
      if (!trimmedLine ||
          trimmedLine.startsWith('/*') ||
          trimmedLine.startsWith('//') ||
          trimmedLine.endsWith('{') ||
          trimmedLine.endsWith('}') ||
          trimmedLine.startsWith('*') ||
          trimmedLine.endsWith('*/')) {
        continue;
      }
      // If line has a colon (property: value) but doesn't end with ; or }
      if (trimmedLine.includes(':') && !trimmedLine.endsWith(';') && !trimmedLine.endsWith('}')) {
        return 'Missing semicolon detected';
      }
    }

    return null;
  }, [value]);

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const helperText = `/* Styles apply to this element */
/* Example: */
/* background: red; */
/* transform: rotate(5deg); */`;

  return (
    <div className="border border-gray-200 rounded overflow-hidden">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        <Code className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-700 flex-1">
          {field.label || 'Custom CSS'}
        </span>
        {value && value.trim() && (
          <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
            Active
          </span>
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-3 space-y-2 bg-white">
          {/* Selector Display */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Selector:</span>
            <code className="px-1.5 py-0.5 bg-gray-100 rounded font-mono text-gray-700">
              {selector}
            </code>
          </div>

          {/* CSS Editor */}
          <div className="relative">
            <textarea
              value={value ?? ''}
              onChange={handleChange}
              disabled={disabled}
              placeholder={helperText}
              spellCheck={false}
              className={`
                w-full px-3 py-2 text-xs border rounded
                font-mono
                bg-[#1e1e1e] text-gray-100
                placeholder-gray-500
                min-h-[100px] resize-y
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${cssWarning ? 'border-yellow-500' : 'border-gray-700'}
              `}
              style={{
                fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace",
                lineHeight: '1.5',
                tabSize: 2,
              }}
              rows={6}
            />
          </div>

          {/* CSS Warning */}
          {cssWarning && (
            <div className="flex items-center gap-1.5 text-xs text-yellow-600 bg-yellow-50 px-2 py-1.5 rounded">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{cssWarning}</span>
            </div>
          )}

          {/* Helper Text */}
          <p className="text-xs text-gray-500 leading-relaxed">
            Enter CSS properties without the selector. Properties will be applied directly to this element.
          </p>
        </div>
      )}
    </div>
  );
}
